import { CellContext, Row } from '@tanstack/react-table';
import { getDatePeriodString } from '../../../utils';
import { memoize } from '../../../utils/memoize';
import { ROW_UNIQUE_KEY_NAME, UUIDType } from '../../../utils/randomUniqueKey';

export const getFormTableFieldPath = <RowData>(
  context: CellContext<RowData, any>,
): readonly (string | number)[] => {
  /**
   * `context.column.id` is derived from accessorKey or id. If neither is defined then
   * it is derived from `columnDef.header` which cannot be used to point to a field.
   *
   * If you define column with accessorFn instead of accessorKey, make sure you define the id:
   *
   * columnHelper.accessor(
   *   'subCategory', <-- accessorKey
   *   {
   *     // `id` is not necessary if accessorKey already represents a field name
   *   }
   * )
   *  --- OR ---
   * columnHelper.accessor(
   *   (rowData) => `${rowData.subCategory} - ${rowData.subCategoryName}`, <-- accessorFn that combines field values into display value
   *   {
   *     id: 'subCategory',
   *   }
   * )
   */
  if (
    !(context.column.columnDef as any).accessorKey &&
    !context.column.columnDef.id
  ) {
    console.error(context.column.columnDef);
    throw new Error(
      `Expecting either "accessorKey" or "id" to be defined in columnDef. Check the above column definition.`,
    );
  }

  const path: (string | number)[] = [context.table.options.name];

  context.row.getParentRows().forEach(({ index }) => {
    path.push(index, 'children');
  });

  path.push(context.row.index, ...context.column.id.split('.'));

  return path as any;
};

export const getFormTableFieldName = memoize(
  <RowData>(context: CellContext<RowData, any>) =>
    getFormTableFieldPath(context).join('.') as `${string}.${number}.${string}`,
);

export const getDisplayValueWithFormValueAttached = <T>(
  formValue: T,
  stringValue: string,
) =>
  stringValue
    ? Object.assign(new String(stringValue), { formValue })
    : undefined;

export const getValidPeriodDisplayValueWithFormValueAttached = <
  T extends [string | null, string | null],
>(
  formValue: T,
) =>
  getDisplayValueWithFormValueAttached(
    formValue,
    getDatePeriodString(...formValue),
  );

export const getRowPathIndexes = (row: Row<any>) =>
  row
    .getParentRows()
    .concat(row)
    .map(({ index }) => index);

export type TreeNode<D extends { [key: string]: any }> = D & {
  [key: string]: any;
  [ROW_UNIQUE_KEY_NAME]: UUIDType;
  children: TreeNode<D>[];
};

/**
 * Create hierarchical list of nodes with O(n) time complexity
 */
export const listToTree = <
  ListNode extends {
    [key: string]: any;
    [ROW_UNIQUE_KEY_NAME]: string;
    parentKey: string | null;
  },
  TransformedData extends { [key: string]: any },
>(
  list: ListNode[],
  transform?: (node: ListNode, isRootLevel: boolean) => TransformedData,
): TreeNode<TransformedData>[] => {
  const keyIndexMap: { [key: string]: number } = {};
  const roots: TreeNode<TransformedData>[] = [];
  const cache: Array<{
    source: ListNode;
    children: TreeNode<TransformedData>[];
  }> = [];

  for (const [i, source] of list.entries()) {
    keyIndexMap[source[ROW_UNIQUE_KEY_NAME]] = i;
    cache.push({
      source,
      children: [] as TreeNode<TransformedData>[],
    });
  }

  for (const { source, children } of cache) {
    const { parentKey, [ROW_UNIQUE_KEY_NAME]: key } = source;

    const parentNode = parentKey && cache[keyIndexMap[parentKey]];
    const treeNode: TreeNode<TransformedData> = {
      ...(transform
        ? transform(source, !parentNode)
        : (source as TreeNode<any>)),
      children,
      [ROW_UNIQUE_KEY_NAME]: key,
    };

    if (parentNode) {
      parentNode.children.push(treeNode);
    } else {
      roots.push(treeNode);
    }
  }

  return roots;
};
